import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useCameraStream } from '@/mobile/hooks/useCameraStream';
import { useOCRScan } from '@/mobile/hooks/useOCRScan';
import { useCreatePagamento } from '@/lib/hooks/pagamentos';
import { useAuth } from '@/lib/auth';
import { ScanViewfinder } from './ScanViewfinder';
import { ScanProcessing } from './ScanProcessing';
import { OCRReview } from './OCRReview';
import { ScanErrorState } from './ScanErrorState';
import type { DocType, ScanStage } from './types';
import type { ReviewFormValues } from './OCRReviewForm';

interface ScanModuleProps {
  onClose: () => void;
}

// Mensagem de erro específica para cota esgotada, usada como estado persistente
const QUOTA_ERROR_MSG =
  'A cota mensal de OCR foi atingida. Aguarde o próximo mês para escanear novos documentos.';

/**
 * Máquina de estados do fluxo de scan (Fase 3.2 — pipeline real):
 *   view → processing → review → (cria pagamento) → fecha
 *                ↓
 *            error  (câmera negada / sem suporte / 429)
 *
 * Erros 429 redirecionam para o stage 'error' com mensagem de cota.
 * Erros 5xx/network exibem toast e voltam para view (viewfinder).
 */
export function ScanModule({ onClose }: ScanModuleProps) {
  const { user } = useAuth();
  const wsId = user?.effectiveWorkspaceId ?? null;

  const [stage, setStage] = useState<ScanStage>('view');
  const [docType, setDocType] = useState<DocType>('nfe');
  const [shot, setShot] = useState<string | null>(null);
  const [quotaError, setQuotaError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const qc = useQueryClient();

  // Câmera ativa somente no estágio view
  const cameraActive = stage === 'view';
  const {
    videoRef,
    canvasRef,
    status: cameraStatus,
    capture,
    stopStream,
  } = useCameraStream(cameraActive);

  const {
    status: ocrStatus,
    result: ocrResult,
    r2Key,
    errorKind,
    run: runOCR,
    reset: resetOCR,
  } = useOCRScan();

  const createPagamento = useCreatePagamento(wsId);

  // ── Transição view → processing ──────────────────────────────────────────
  const startOCR = useCallback(
    (dataUrl: string) => {
      if (!wsId) {
        toast.error('Nenhum workspace ativo. Faça login novamente.');
        return;
      }
      setShot(dataUrl);
      stopStream();
      setStage('processing');
      runOCR(wsId, docType, dataUrl);
    },
    [wsId, docType, runOCR, stopStream],
  );

  const handleCapture = useCallback(() => {
    const dataUrl = capture();
    if (dataUrl) startOCR(dataUrl);
  }, [capture, startOCR]);

  const handleGallery = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target?.result;
        if (typeof dataUrl !== 'string') return;
        startOCR(dataUrl);
      };
      reader.readAsDataURL(file);
    };
    input.click();
  }, [startOCR]);

  // ── Cancelar processing → voltar view ────────────────────────────────────
  const handleCancelProcessing = useCallback(() => {
    resetOCR();
    setShot(null);
    setQuotaError(false);
    setStage('view');
  }, [resetOCR]);

  // ── Confirmar review → createPagamento ───────────────────────────────────
  const handleConfirm = useCallback(
    async (values: ReviewFormValues) => {
      if (!wsId || !r2Key) return;

      const rawValor = values.valorTotal.replace(',', '.');
      // Derivar competencia YYYY-MM de dataEmissao
      const competencia = values.dataEmissao?.slice(0, 7) ?? '';

      // Mapear forma de pagamento do form (pt-BR) para enum do backend
      const formaPagamentoMap: Record<string, 'PIX' | 'TED' | 'DOC' | 'BOLETO' | 'DINHEIRO' | 'CHEQUE' | undefined> = {
        'PIX': 'PIX',
        'Transferência': 'TED',
        'Boleto bancário': 'BOLETO',
        'Cartão corporativo': undefined,
        'Dinheiro': 'DINHEIRO',
        'Cheque': 'CHEQUE',
      };
      // Para boleto DocType, forçar BOLETO se não mapeado
      const formaPagamento =
        formaPagamentoMap[values.formaPagamento] ??
        (docType === 'boleto' ? 'BOLETO' : undefined);

      setIsSaving(true);
      try {
        await createPagamento.mutateAsync({
          competencia,
          credorCpfCnpj: values.emitenteCnpj,
          credorNome: values.emitenteNome,
          valor: rawValor,
          dataPagamento: values.dataEmissao,
          categoriaDespesa: docType !== 'extrato' ? values.categoria || undefined : undefined,
          comprovanteR2Key: r2Key,
          formaPagamento,
        });

        // Invalida lista + aggregate para refresh imediato
        qc.invalidateQueries({ queryKey: ['pagamentos', wsId] });

        toast.success(`Lançamento adicionado · ${values.emitenteNome}`);
        onClose();
      } catch {
        toast.error('Não foi possível salvar. Tente novamente.');
      } finally {
        setIsSaving(false);
      }
    },
    [wsId, r2Key, docType, createPagamento, qc, onClose],
  );

  // ── Descartar review → voltar view ───────────────────────────────────────
  const handleDiscard = useCallback(() => {
    resetOCR();
    setShot(null);
    setQuotaError(false);
    setStage('view');
  }, [resetOCR]);

  // ── Retry error state ─────────────────────────────────────────────────────
  const handleRetry = useCallback(() => {
    resetOCR();
    setShot(null);
    setQuotaError(false);
    setStage('view');
  }, [resetOCR]);

  // Arquivo selecionado no error state → processing
  const handleFileFromError = useCallback(
    (dataUrl: string) => {
      setQuotaError(false);
      startOCR(dataUrl);
    },
    [startOCR],
  );

  // ── Transições reativas ───────────────────────────────────────────────────
  // OCR concluído → avançar para review
  if (ocrStatus === 'done' && ocrResult && stage === 'processing') {
    setStage('review');
  }

  // OCR com erro → tratar por tipo
  if (ocrStatus === 'error' && stage === 'processing') {
    if (errorKind === 'quota') {
      // 429: redireciona para error stage com mensagem de cota
      setQuotaError(true);
      setStage('error');
    } else {
      // server/network: toast já disparado no hook, volta para view
      resetOCR();
      setShot(null);
      setStage('view');
    }
  }

  // Câmera negada/sem suporte
  const effectiveStage: ScanStage =
    stage === 'view' &&
    (cameraStatus === 'denied' || cameraStatus === 'unsupported')
      ? 'error'
      : stage;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        overflow: 'hidden',
      }}
    >
      {effectiveStage === 'view' && (
        <ScanViewfinder
          videoRef={videoRef}
          canvasRef={canvasRef}
          cameraStatus={cameraStatus}
          docType={docType}
          onDocTypeChange={setDocType}
          onCapture={handleCapture}
          onGallery={handleGallery}
          onClose={onClose}
        />
      )}

      {effectiveStage === 'processing' && (
        <ScanProcessing shot={shot} onCancel={handleCancelProcessing} />
      )}

      {effectiveStage === 'review' && ocrResult && (
        <OCRReview
          ocr={ocrResult}
          docType={docType}
          shot={shot}
          isSaving={isSaving}
          onConfirm={handleConfirm}
          onDiscard={handleDiscard}
          onBack={handleDiscard}
        />
      )}

      {effectiveStage === 'error' && (
        <ScanErrorState
          cameraStatus={cameraStatus}
          quotaMessage={quotaError ? QUOTA_ERROR_MSG : undefined}
          onRetry={handleRetry}
          onFileSelected={handleFileFromError}
          onClose={onClose}
        />
      )}
    </div>
  );
}
